import { dupQwanta, persistQwantum } from 'qwanyx';
import { isAncestor } from '../utils/stackUtils';

export class StackOperations {
    constructor(debug = false, stackRootId = null) {
        this.debug = debug;
        this.stackRootId = stackRootId;
    }

    setStackRootId(stackRootId) {
        this.stackRootId = stackRootId;
    }

    // Helper to construct complete item data for local updates
    constructItemData(itemId, overrideProps = {}) {
        const now = new Date().toISOString();
        return {
            _id: itemId,
            ed: this.stackRootId,
            di: null,
            pa: null,
            title: 'New Item',
            firstname: null,
            name: null,
            brief: null,
            isFolder: false,
            vi: null,
            icon: null,
            createdAt: now,
            updatedAt: now,
            ...overrideProps
        };
    }

    async duplicateItem(itemId, cardDataMap) {
        if (!itemId) {
            throw new Error("No item ID provided for duplication");
        }

        const itemData = cardDataMap[itemId];
        if (!itemData) {
            throw new Error(`Item data not found for: ${itemId}`);
        }

        try {
            const result = await dupQwanta(itemId);

            if (result.success && result.newId) {
                // Construct the new item data locally
                const newItemData = this.constructItemData(result.newId, {
                    ...itemData,
                    _id: result.newId,
                    title: `${itemData.title} (Copy)`,
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString()
                });

                if (this.debug) {
                    console.log(`Successfully duplicated item: ${itemId} -> ${result.newId}`);
                }

                return {
                    success: true,
                    newId: result.newId,
                    parentId: itemData.ed,
                    newItemData // Return complete item data for local update
                };
            } else {
                throw new Error(result.error || "Unknown duplication error");
            }
        } catch (error) {
            console.error("Error during duplication:", error);
            throw error;
        }
    }

    // Enhanced createNewItem that returns complete item data
    async createNewItem(sourceId, title = null, parentId = null) {
        if (!sourceId) {
            throw new Error("Source ID is required for new item creation");
        }

        const targetParentId = parentId || this.stackRootId;
        
        if (!targetParentId) {
            throw new Error("Parent ID is required for new item creation");
        }

        try {
            const overrideProps = { ed: targetParentId };

            if (title && title.trim()) {
                overrideProps.title = title.trim();
            }

            if (this.debug) {
                console.log("Creating new item with properties:", {
                    sourceId,
                    parentId: targetParentId,
                    overrideProps
                });
            }

            const result = await dupQwanta(sourceId, null, overrideProps, false, title);

            if (result.success && result.newId) {
                // Construct complete item data locally
                const newItemData = this.constructItemData(result.newId, {
                    ed: targetParentId,
                    title: title?.trim() || 'New Item',
                    isFolder: false
                });

                if (this.debug) {
                    console.log(`Successfully created new item from template: ${sourceId} -> ${result.newId} with parent: ${targetParentId}`);
                    if (title) {
                        console.log(`Assigned title: "${title}"`);
                    }
                }

                return {
                    success: true,
                    newId: result.newId,
                    parentId: targetParentId,
                    newItemData // Return complete item data for local update
                };
            } else {
                throw new Error(result.error || "Unknown creation error");
            }
        } catch (error) {
            console.error("Error during new item creation:", error);
            throw error;
        }
    }

    async moveItem(draggedItemId, targetId, sourceParentId, cardDataMap) {
        // Validation checks
        if (draggedItemId === targetId) {
            throw new Error("Cannot move item: Item cannot be dropped on itself");
        }

        // Check for circular reference
        const willCreateCircularReference = await isAncestor(draggedItemId, targetId);
        if (willCreateCircularReference) {
            throw new Error("Cannot move item: This would create a circular reference");
        }

        try {
            // Update the dragged item's parent
            await persistQwantum(draggedItemId, "ed", targetId, false);

            // Construct updated item data locally
            const originalItemData = cardDataMap[draggedItemId];
            const updatedItemData = {
                ...originalItemData,
                ed: targetId,
                updatedAt: new Date().toISOString()
            };

            if (this.debug) {
                console.log(`Successfully moved item ${draggedItemId} from ${sourceParentId} to ${targetId}`);
            }

            return {
                success: true,
                draggedItemId,
                targetId,
                sourceParentId,
                updatedItemData // Return updated item data for local update
            };
        } catch (error) {
            console.error("Failed to move item:", error);
            throw new Error(`Error moving item: ${error.message}`);
        }
    }

    async updateItemTitle(itemId, newTitle, cardDataMap) {
        if (!itemId || !newTitle || !newTitle.trim()) {
            throw new Error("Item ID and title are required");
        }

        try {
            await persistQwantum(itemId, "title", newTitle.trim(), false);
            
            // Construct updated item data locally
            const originalItemData = cardDataMap[itemId];
            const updatedItemData = {
                ...originalItemData,
                title: newTitle.trim(),
                updatedAt: new Date().toISOString()
            };

            if (this.debug) {
                console.log("Title updated in database:", newTitle);
            }

            return { 
                success: true, 
                updatedItemData // Return updated item data for local update
            };
        } catch (error) {
            console.error("Failed to update title in database:", error);
            throw error;
        }
    }

    async makeItemFolder(itemId, cardDataMap) {
        try {
            await persistQwantum(itemId, "isFolder", true, false);
            
            // Construct updated item data locally
            const originalItemData = cardDataMap[itemId];
            const updatedItemData = {
                ...originalItemData,
                isFolder: true,
                updatedAt: new Date().toISOString()
            };
            
            if (this.debug) {
                console.log(`Made item ${itemId} a folder`);
            }

            return { 
                success: true, 
                updatedItemData // Return updated item data for local update
            };
        } catch (error) {
            console.error("Failed to make item a folder:", error);
            throw error;
        }
    }

    async updateViewPreference(itemId, viewId, cardDataMap) {
        try {
            await persistQwantum(itemId, "vi", viewId);
            
            // Construct updated item data locally
            const originalItemData = cardDataMap[itemId];
            const updatedItemData = {
                ...originalItemData,
                vi: viewId,
                updatedAt: new Date().toISOString()
            };
            
            if (this.debug) {
                console.log(`Saved view preference "${viewId}" for item ${itemId}`);
            }

            return { 
                success: true, 
                updatedItemData // Return updated item data for local update
            };
        } catch (error) {
            console.error("Error saving view preference:", error);
            throw error;
        }
    }

    // New method to delete item (for completeness)
    async deleteItem(itemId, cardDataMap) {
        try {
            // Implement actual deletion logic here
            // await deleteQwantum(itemId); // hypothetical delete function
            
            if (this.debug) {
                console.log(`Deleted item ${itemId}`);
            }

            return { 
                success: true, 
                deletedItemId: itemId
            };
        } catch (error) {
            console.error("Failed to delete item:", error);
            throw error;
        }
    }
}